import { validateId } from '~/repositories/utils';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import { uploadSessionRepository } from './uploadSessionRepository';
import type { UploadFileOptions } from './uploadSessionRepository';
import type {
  AnnouncementsDto,
  GetAnnouncementTargetsRequestDto,
  HydratedAnnouncementDto,
  HydratedAnnouncementTargetsDto,
  SendAnnouncementRequestDto,
} from '~/swagger/portal/Api';

const ANNOUNCEMENT_URL = '/api/announcements';
const ANNOUNCEMENT_UPLOAD_SESSION_URL = '/api/announcements/upload-sessions';

export type AnnouncementListQuery = {
  'sendAt.start'?: string;
  'sendAt.end'?: string;
  portfolioIds?: string[];
  propertyIds?: string[];
  unitIds?: string[];
  leaseIds?: string[];
  vendorIds?: string[];
  ownerIds?: string[];
};

export interface AnnouncementSendFailureDto {
  targetId: string;
  targetType: string;
  channel: string;
  error: string;
}

export interface SendAnnouncementResultDto {
  id?: string;
  totalTargets: number;
  successCount: number;
  failureCount: number;
  failures: AnnouncementSendFailureDto[];
  isFullSuccess: boolean;
  isPartialSuccess: boolean;
  isFullFailure: boolean;
}

export class AnnouncementRepository extends BaseRestRepository {
  public async uploadAnnouncementAttachments(files: File[], opts?: UploadFileOptions): Promise<string[]> {
    if (files.length === 0) return [];

    return uploadSessionRepository.uploadFiles(ANNOUNCEMENT_UPLOAD_SESSION_URL, files, opts);
  }

  public async getAnnouncementTargets(filter?: GetAnnouncementTargetsRequestDto): Promise<HydratedAnnouncementTargetsDto> {
    const url = ANNOUNCEMENT_URL + '/targets';
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(filter),
    });
    return this.getJsonResponse(response);
  }

  public async getAnnouncements(filter?: AnnouncementListQuery): Promise<AnnouncementsDto[]> {
    const url = urlWithQuery(ANNOUNCEMENT_URL, filter);

    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getAnnouncementDetails(announcementId: string): Promise<HydratedAnnouncementDto> {
    validateId(announcementId);
    const url = `${ANNOUNCEMENT_URL}/${announcementId}`;

    const response = await this.fetchWithAuth(url);

    return this.getJsonResponse(response);
  }

  public async createAnnouncementWithJSON(announcement: unknown): Promise<HydratedAnnouncementDto> {
    const response = await this.fetchWithAuth(ANNOUNCEMENT_URL, {
      method: 'POST',
      body: JSON.stringify(announcement),
    });
    return this.getJsonResponse(response);
  }

  public async sendAnnouncementByIds(data: SendAnnouncementRequestDto, showErrorMessage = true): Promise<SendAnnouncementResultDto> {
    const url = `${ANNOUNCEMENT_URL}/send`;
    const response = await this.fetchWithAuth(
      url,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      showErrorMessage
    );
    return this.getJsonResponse(response);
  }
}

export const announcementRepository = new AnnouncementRepository();
